import { ArrowLeft } from 'lucide-react';
import { ApiError } from '@platform/api-client';
import { Button, EmptyState, Skeleton } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { ProductEditor, type ProductEditorProps } from './editor/ProductEditor.tsx';
import { useActiveCategories, useOptions, useProduct, useTenantDetail } from './useCatalogQueries.ts';
import { LoadFailed } from '../LoadFailed.tsx';
import { usePageContext } from '../usePageContext.ts';

const NOT_FOUND = 404;

export type ProductEditorPageProps = Omit<
  ProductEditorProps,
  'product' | 'tenant' | 'categories' | 'options'
> & {
  api: TypedClient;
  productId: string | null;
};

export const ProductEditorPage = ({ api, productId, ...handlers }: ProductEditorPageProps) => {
  const { t, describeError } = usePageContext();
  const product = useProduct(api, productId);
  const tenant = useTenantDetail(api);
  const categories = useActiveCategories(api);
  const options = useOptions(api);
  const queries = productId === null ? [tenant, categories, options] : [product, tenant, categories, options];
  const failed = queries.find((query) => query.isError);

  if (failed !== undefined && failed.error instanceof ApiError && failed.error.status === NOT_FOUND)
    return (
      <EmptyState
        title={t('productEditor.notFound')}
        action={
          <Button
            icon={<ArrowLeft size={20} aria-hidden="true" className="flip-inline" />}
            variant="outline"
            onClick={handlers.onBack}
          >
            {t('productEditor.backToProducts')}
          </Button>
        }
      />
    );
  if (failed !== undefined)
    return (
      <LoadFailed
        description={describeError(failed.error)}
        onRetry={() => {
          for (const query of queries) void query.refetch();
        }}
      />
    );
  if (
    tenant.data === undefined ||
    categories.data === undefined ||
    options.data === undefined ||
    (productId !== null && product.data === undefined)
  )
    return <Skeleton height="480px" label={t('shell.loading')} />;

  return (
    <ProductEditor
      key={productId ?? 'new'}
      api={api}
      product={product.data ?? null}
      tenant={tenant.data}
      categories={categories.data.items}
      options={options.data.items}
      {...handlers}
    />
  );
};
